import React, { useEffect, useState } from 'react';
import { Spinner, useSimpleSpinner } from '../spinner/index';
import SelectDropdown from '../selectDropdown';
import { contratosFixture } from './fixtures/contratos.fixture';
import { ContrastModeToggle } from '../ContrastModeToggle/ContrastModeToggle';
import Resultados from '../Resultados/resultados';
import './busqueda.css';
import '../Resultados/resultados.css';

interface UserInfo {
  LegalID?: string;
  Nit?: string;
  [key: string]: unknown;
}

interface Anexo {
  Id: number;
  Nombre: string;
  FechaFin: string;
  FechaInicio: string;
  OtrosAspectos: string;
  TipoAcuerdo: string;
  FechaAprobacion: string;
}

interface Contrato {
  Id: number;
  Nombre: string;
  Anexos: Anexo[];
}

interface AcuerdosTarifariosResponse {
  ResponseCode: number;
  ResponseDetail: string;
  Result: {
    Contratos: Contrato[];
    HayAcuerdos: boolean;
  };
}


const Busqueda: React.FC = () => {
  const { loading, startLoading, stopLoading } = useSimpleSpinner();
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [contratoSeleccionado, setContratoSeleccionado] = useState<number | null>(null);
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [anexoSeleccionado, setAnexoSeleccionado] = useState<number | null>(null);
  const [mostrarTabla, setMostrarTabla] = useState(false);
  const [error, setError] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isAuthenticatedBoolean, setIsAuthenticatedBoolean] = useState(false);
  
  useEffect(() => {
    const sharePointElement = document.querySelector('[data-name="Campo de página: Contenido de la página"]');
    if (sharePointElement) {
      sharePointElement.remove();
    }
  }, []);

  useEffect(() => {
    isAuthenticated();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAuthenticated = () => {
    const authenticatedUser = compTools.obtenerInformacionUsuario();
    setUserInfo(authenticatedUser);
    setIsAuthenticatedBoolean(true);
    fetchContratos(authenticatedUser);
  };

  const fetchContratos = async (userData: UserInfo) => {
    startLoading();
    try {
      // Si estamos en modo desarrollo, usamos el fixture
      if (__DEV__ || import.meta.env.MODE === 'development') {
        console.log("Ambiente de pruebas - Usando fixture", { 
          DEV_FLAG: __DEV__, 
          MODE: import.meta.env.MODE, 
          DEV: import.meta.env.DEV, 
          PROD: import.meta.env.PROD 
        });
        
        // Simulamos un pequeño delay para simular la llamada al API
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const data: AcuerdosTarifariosResponse = contratosFixture;
        
        if (data.ResponseCode === 200 && data.Result.Contratos) {
          setContratos(data.Result.Contratos);
        } else {
          setError(true);
        }
        
        stopLoading();
        return;
      }

      let nit;
      //const nit = '41675348';
      
      console.log("Ambiente productivo");
      // En producción, siempre usamos el dato del userData
      // Verificamos si es una empresa (TipoIdentificacion === 2)
       if (userData?.LegalID) {
        nit = userData.LegalID;
      } else {
        // Si no es empresa o no tiene LegalID, manejamos el caso de error
        console.error('Error: No se pudo obtener el NIT del usuario.');
        setError(true);
        stopLoading();
        return; // Salimos de la función para evitar la llamada al API sin NIT
      } 
      
      const response = await fetch(
        `/_vti_bin/Servicios/ServicioTransar.svc/ObtenerAcuerdosTarifarios?nit=${nit}&estado=4&logsAprobacion=true`
      );
      const data: AcuerdosTarifariosResponse = await response.json();
      
      // Verificar que el ResponseCode sea 200 y que Result tenga contratos
      if (data.ResponseCode === 200 && data.Result && data.Result.Contratos) {
        setContratos(data.Result.Contratos);
      } else {
        // Si el ResponseCode no es 200 (ej: 450) o Result es null, mostrar error
        console.error('Error en la respuesta del servicio:', {
          code: data.ResponseCode,
          detail: data.ResponseDetail
        });
        setError(true);
      }
    } catch (err) {
      console.error('Error al cargar contratos:', err);
      setError(true);
    } finally {
      stopLoading();
    }
  };

  
  const handleContratoChange = (value: string | number, data?: Record<string, unknown>) => {
    const contratoId = Number(value);
    setContratoSeleccionado(contratoId);
    
    const contrato = contratos.find(c => c.Id === contratoId);
    setAnexos(contrato ? contrato.Anexos : []);
    setAnexoSeleccionado(null);
    setMostrarTabla(false);
  };

  const handleAnexoChange = (value: string | number, data?: Record<string, unknown>) => {
    const anexoId = Number(value);
    setAnexoSeleccionado(anexoId);
    
    if (anexoId) {
      // Mostramos los datos del anexo seleccionado
      setMostrarTabla(true);
      
      // Hacer scroll hacia abajo para enfocar la sección de resultados después de un pequeño delay
      setTimeout(() => {
        try {
          // Buscar el hr que separa los formularios de los resultados
          const hrElement = document.querySelector('.separador-resultados');
          if (hrElement) {
            const rect = hrElement.getBoundingClientRect();
            const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
            const targetY = currentScrollY + rect.top - 130;
            
            window.scrollTo({
              top: targetY,
              behavior: 'smooth'
            });
          } else {
            // Fallback: scroll hacia la tabla si no se encuentra el hr
            const tablaElement = document.querySelector('#resultadoImagenesDiagnosticas');
            if (tablaElement) {
              tablaElement.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'nearest'
              });
            }
          }
        } catch (error) {
          console.warn('Error al hacer scroll a la sección de resultados:', error);
        }
      }, 100); // Pequeño delay para que la tabla se renderice
    } else {
      setMostrarTabla(false);
    }
  };


  const anexoSeleccionadoData = anexos.find(a => a.Id === anexoSeleccionado);

  return isAuthenticatedBoolean ? (
    <>
      <Spinner isVisible={loading} />
      <div className="header-section">
        <h1 className="main-title">{window.CONST?.AnexosTarifarios?.titulo || 'Consulta de Anexos Tarifarios'}</h1>
        <p className="main-description">
          {window.CONST?.AnexosTarifarios?.descripcion || 'Aquí podrás ver los anexos aprobados.'}
        </p>
      </div>
      
      <div className="gap-40">
        <div className="contenedor activo">
          <div className="numbercontainer">
            <span className="digit">1</span>
          </div>
          <div className="form-container">
            <p style={{ marginBottom: '20px', color: '#333333ff', width: '100%' }}>
              {window.CONST?.AnexosTarifarios?.instruccionBusqueda || 'Selecciona el contrato activo sobre el cual deseas consultar los anexos tarifarios.'}
            </p>

            {/* Mostrar fallback si no hay contratos disponibles */}
            {contratos.length === 0 && !loading && !error ? (
              <div className="no-data">
                <p>{window.CONST?.AnexosTarifarios?.noDataAnexos || 'No tienes Anexos Tarifarios aprobados'}</p>
              </div>
            ) : (
              <div className="form-column" style={{ paddingTop: '40px' }}>
                <SelectDropdown
                  label={window.CONST?.AnexosTarifarios?.labelContratos || 'Contratos activos'}
                  options={contratos.map(c => ({
                    value: c.Id,
                    label: c.Nombre,
                    data: c as unknown as Record<string, unknown>,
                  }))}
                  value={contratoSeleccionado ?? ''}
                  placeholder={window.CONST?.AnexosTarifarios?.placeholderContratos || 'Seleccione un contrato'}
                  onChange={handleContratoChange}
                />
                
                <SelectDropdown
                  label={window.CONST?.AnexosTarifarios?.labelAnexos || 'Anexos tarifarios aprobados'}
                  options={anexos.map(a => ({
                    value: a.Id,
                    label: a.Nombre,
                    data: a as unknown as Record<string, unknown>,
                  }))}
                  value={anexoSeleccionado ?? ''}
                  placeholder={window.CONST?.AnexosTarifarios?.placeholderAnexos || 'Seleccione un anexo'}
                  disabled={contratoSeleccionado === null}
                  onChange={handleAnexoChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Tabla de datos del anexo - componente Resultados */}
      {mostrarTabla && anexoSeleccionadoData && (
        <Resultados 
          anexoSeleccionadoData={anexoSeleccionadoData}
          nit={userInfo?.LegalID || ''}
          contratoNombre={contratos.find(c => c.Id === contratoSeleccionado)?.Nombre || ''}
        />
      )}
      
      <ContrastModeToggle />
    </>
  ) : (
    /* Vista de carga o no autenticado */
    <>
      <Spinner isVisible={true} text={window.CONST?.AnexosTarifarios?.cargandoUsuario || 'Cargando información del usuario...'} />
      <div style={{ textAlign: 'center', padding: '20px' }}></div>
    </>
  );
};

export default Busqueda;